import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// Live open/closed state for every store, so a store opening or closing shows
// up on screen without a refresh.
//
// ONE listener for the whole app, on ONE document (public/storeStatus, written
// by the backend in services/liveStatus.js). Firestore bills a listener one
// read when it starts and one per change after that - so this costs one read
// per visit plus one per open/close while someone is on the site, no matter
// how many stores or pages there are. Components must read it through this
// context rather than opening listeners of their own.
//
// It only ever refines what the API already returned: a store that isn't in
// the document yet (never toggled since this shipped), or any listener
// failure, falls back to the isStoreOpen the page loaded with. Ordering is
// still enforced server-side (orderController rejects closed stores) - this is
// display only.

const StoreStatusContext = createContext({ liveOpen: {}, isStoreOpen: (storeId, fallback) => !!fallback });

export const useStoreStatus = () => useContext(StoreStatusContext);

export const StoreStatusProvider = ({ children }) => {
  const [liveOpen, setLiveOpen] = useState({});

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'public', 'storeStatus'),
      (snapshot) => {
        const stores = snapshot.exists() ? snapshot.data().stores || {} : {};
        setLiveOpen(
          Object.fromEntries(Object.entries(stores).map(([storeId, entry]) => [storeId, !!entry?.isOpen]))
        );
      },
      (error) => {
        // Keep the last known values; pages fall back to API data for the rest.
        console.warn('Live store status unavailable:', error.code || error.message);
      }
    );
    return unsubscribe;
  }, []);

  /** Live state when known, otherwise the value the page loaded from the API. */
  const isStoreOpen = useCallback(
    (storeId, fallback) => (storeId in liveOpen ? liveOpen[storeId] : !!fallback),
    [liveOpen]
  );

  const value = useMemo(() => ({ liveOpen, isStoreOpen }), [liveOpen, isStoreOpen]);

  return <StoreStatusContext.Provider value={value}>{children}</StoreStatusContext.Provider>;
};
